import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogClose } from "@/components/ui/dialog";
import { DateRangePicker, type DateRange } from "@/components/ui/date-range-picker";
import { format, subMonths } from "date-fns";
import { Calendar as CalendarIcon, Download, X, ArrowRight, ArrowUpRight, DollarSign, FolderOpen, Lightbulb, Trash, ArrowUpCircle } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import CostBreakdownChart from "@/components/cost-management/cost-breakdown-chart";
import SavingsOpportunities from "@/components/cost-management/savings-opportunities";
import SpendingTrends from "@/components/cost-management/spending-trends";
import BudgetUtilization from "@/components/cost-management/budget-allocation";
import { Application, License, OptimizationRecommendation } from "@shared/schema";
import { formatCurrency, getRecommendationTypeIcon } from "@/lib/utils";
import RecommendationDetailDialog from "@/components/cost-management/recommendation-detail-dialog";

export default function CostManagement() {
  const [dateRange, setDateRange] = useState<DateRange>({
    from: subMonths(new Date(), 3),
    to: new Date()
  });
  const [selectedRecommendationId, setSelectedRecommendationId] = useState<number | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  
  // Fetch data
  const { data: applications = [] } = useQuery<Application[]>({
    queryKey: ['/api/applications'],
  });
  
  const { data: licenses = [] } = useQuery<License[]>({
    queryKey: ['/api/licenses'],
  });
  
  const { data: recommendations = [] } = useQuery<OptimizationRecommendation[]>({
    queryKey: ['/api/recommendations'],
  });
  
  // Function to handle date range change
  const handleDateRangeChange = (newRange: DateRange) => {
    setDateRange(newRange);
  };
  
  const handleViewRecommendation = (id: number) => {
    setSelectedRecommendationId(id);
    setDialogOpen(true);
  };
  
  // Function to generate and export cost report
  const exportCostReport = () => {
    // Create report data
    const reportData = {
      period: `${format(dateRange.from, 'MMM d, yyyy')} - ${format(dateRange.to, 'MMM d, yyyy')}`,
      generatedOn: format(new Date(), 'MMM d, yyyy'),
      totalApplications: applications.length,
      totalLicenses: licenses.length,
      totalCost: licenses.reduce((sum, license) => sum + license.cost, 0),
      costByCategory: {},
      costByVendor: {},
      licenseDetails: licenses.map(license => {
        const app = applications.find(a => a.id === license.applicationId);
        return {
          name: license.name,
          application: app?.name || 'Unknown',
          vendor: app?.vendor || 'Unknown',
          category: app?.category || 'Other',
          cost: license.cost,
          renewalDate: format(new Date(license.renewalDate), 'MMM d, yyyy')
        };
      })
    };
    
    // Convert to CSV for download
    const csvHeaders = "License,Application,Vendor,Category,Cost,Renewal Date\n";
    const csvRows = reportData.licenseDetails.map(license => 
      `"${license.name}","${license.application}","${license.vendor}","${license.category}",${license.cost},"${license.renewalDate}"`
    ).join('\n');
    const csvContent = csvHeaders + csvRows;
    
    // Create download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `cost-report-${format(new Date(), 'yyyy-MM-dd')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between">
          <div className="mb-4 md:mb-0">
            <h2 className="text-2xl font-bold text-slate-800">Cost Management</h2>
            <p className="text-sm text-slate-500 mt-1">Analyze spending and identify optimization opportunities</p>
          </div>
          <div className="flex gap-3">
            {/* Date Period Picker */}
            <DateRangePicker
              value={dateRange}
              onChange={handleDateRangeChange}
              placeholder="Select date range"
              className="w-auto"
            />
            
            {/* Export Report Dialog */}
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm">
                  <Download className="h-4 w-4 mr-2" />
                  Export Report
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>Export Cost Report</DialogTitle>
                </DialogHeader>
                <div className="flex flex-col space-y-4 py-4">
                  <div className="flex flex-col space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <span className="text-sm font-medium">Report Period</span>
                        <p className="text-sm text-muted-foreground">
                          {format(dateRange.from, 'MMM d, yyyy')} - {format(dateRange.to, 'MMM d, yyyy')}
                        </p>
                      </div>
                      <div>
                        <span className="text-sm font-medium">Total Cost</span>
                        <p className="text-sm text-muted-foreground">
                          {formatCurrency(licenses.reduce((sum, license) => sum + license.cost, 0))}
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <span className="text-sm font-medium">Applications</span>
                        <p className="text-sm text-muted-foreground">{applications.length}</p>
                      </div>
                      <div>
                        <span className="text-sm font-medium">Licenses</span>
                        <p className="text-sm text-muted-foreground">{licenses.length}</p>
                      </div>
                    </div>
                    <div className="pt-2 flex gap-2">
                      <DialogClose asChild>
                        <Button variant="outline" className="flex-1">
                          Cancel
                        </Button>
                      </DialogClose>
                      <DialogClose asChild>
                        <Button onClick={exportCostReport} className="flex-1">
                          <Download className="h-4 w-4 mr-2" />
                          Download CSV
                        </Button>
                      </DialogClose>
                    </div>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>
      
      {/* Cost Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="lg:col-span-2">
          <CostBreakdownChart startDate={dateRange.from} endDate={dateRange.to} />
        </div>
        <div>
          <SavingsOpportunities />
        </div>
      </div>
      
      {/* Cost Details Tabs */}
      <Tabs defaultValue="trends" className="mb-6">
        <TabsList>
          <TabsTrigger value="trends">Spending Trends</TabsTrigger>
          <TabsTrigger value="budget">Budget Utilization</TabsTrigger>
          <TabsTrigger value="recommendations">Optimization Recommendations</TabsTrigger>
        </TabsList>
        
        <TabsContent value="trends" className="mt-4">
          <SpendingTrends startDate={dateRange.from} endDate={dateRange.to} />
        </TabsContent>
        
        <TabsContent value="budget" className="mt-4">
          <BudgetUtilization startDate={dateRange.from} endDate={dateRange.to} />
        </TabsContent>
        
        <TabsContent value="recommendations" className="mt-4">
          <div className="rounded-md border">
            <div className="p-4 bg-white">
              <h3 className="text-lg font-semibold mb-2">Cost Optimization Recommendations</h3>
              <p className="text-sm text-slate-500">Review and implement recommendations to optimize your spending</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 border-y">
                    <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider py-3 px-4">Recommendation</th>
                    <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider py-3 px-4">Description</th>
                    <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider py-3 px-4">Application</th>
                    <th className="text-right text-xs font-medium text-slate-500 uppercase tracking-wider py-3 px-4">Potential Savings</th>
                    <th className="text-center text-xs font-medium text-slate-500 uppercase tracking-wider py-3 px-4">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {recommendations.map(recommendation => {
                    const app = recommendation.applicationId ? 
                      applications.find(a => a.id === recommendation.applicationId) : null;
                    const { iconName, color } = getRecommendationTypeIcon(recommendation.title);
                    
                    return (
                      <tr key={recommendation.id} className="hover:bg-slate-50">
                        <td className="py-3 px-4">
                          <div className="flex items-center">
                            <div className={`w-8 h-8 rounded-full ${color} flex items-center justify-center mr-3 flex-shrink-0`}>
                              {(() => {
                                switch (iconName) {
                                  case 'DollarSign': return <DollarSign className="h-4 w-4" />;
                                  case 'FolderOpen': return <FolderOpen className="h-4 w-4" />;
                                  case 'Trash': return <Trash className="h-4 w-4" />;
                                  case 'ArrowUpCircle': return <ArrowUpCircle className="h-4 w-4" />;
                                  default: return <Lightbulb className="h-4 w-4" />;
                                }
                              })()}
                            </div>
                            <span className="text-sm font-medium">{recommendation.title}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-sm max-w-md">
                          <div className="truncate">
                            {recommendation.description}
                          </div>
                        </td>
                        <td className="py-3 px-4 text-sm">
                          {app ? app.name : 'Multiple applications'}
                        </td>
                        <td className="py-3 px-4 text-sm text-right text-green-600 font-medium">
                          {formatCurrency(recommendation.potentialSavings)}/month
                        </td>
                        <td className="py-3 px-4 text-center">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleViewRecommendation(recommendation.id)}
                            className="text-primary text-xs font-medium"
                          >
                            View Details
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                  
                  {recommendations.length === 0 && (
                    <tr>
                      <td colSpan={5} className="py-6 text-center text-slate-500">
                        No recommendations available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>
      </Tabs>
      
      {/* Recommendation Detail Dialog */}
      <RecommendationDetailDialog
        recommendationId={selectedRecommendationId}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
      />
    </div>
  );
}
